import { IconBuilding, IconSearch, IconUserCircle } from '@maia/icons';
import { Input, Pagination, Table, ToggleButtonGroup } from '@maia/ui';
import { useQuery } from '@tanstack/react-query';
import { debounce } from 'es-toolkit';
import { useEffect } from 'react';
import { useFilter } from '../../../utils/filter/filter';
import { Customers, getCustomersOptions } from './queryOptions';
import { useCustomerTable } from './tableOptions';

type CustomersTableProps = {
  customers: Customers;
};

export function CustomersTable(props: CustomersTableProps) {
  const { customers } = props;

  const { state, dispatch: setSearch, mappedState } = useFilter('/customers');
  const { data } = useQuery(getCustomersOptions(mappedState, customers));
  const table = useCustomerTable(data);

  const { sorting } = table.getState();

  useEffect(() => {
    setSearch({ type: 'setSort', payload: sorting });
  }, [setSearch, sorting]);

  const debouncedQuery = debounce(
    (payload: string) => setSearch({ type: 'setQuery', payload }),
    500,
  );

  return (
    <Table
      table={table}
      className="grid-cols-[min-content_auto_auto_auto_min-content]"
      filterSlot={
        <div className="flex gap-x-24">
          <Input
            icon={<IconSearch />}
            placeholder="Zoeken"
            className="w-full max-w-[550px] mr-auto"
            onChange={(event) => debouncedQuery(event.target.value)}
            defaultValue={state.search}
          />
          <ToggleButtonGroup
            defaultSelectedKey={
              state.filter?.find(([key]) => key === 'customer')?.[1]
            }
            items={[
              { label: 'Alles', value: '' },
              {
                label: <IconUserCircle className="size-16" />,
                value: 'consumer',
              },
              {
                label: <IconBuilding className="size-16" />,
                value: 'business',
              },
            ]}
            onChange={(customer) =>
              setSearch({
                type: 'setFilter',
                payload: ['type', customer],
              })
            }
          />
          <ToggleButtonGroup
            defaultSelectedKey={
              state.filter?.find(([key]) => key === 'status')?.[1]
            }
            items={[
              { label: 'Alle', value: '' },
              { label: 'Actief', value: 'active' },
              { label: 'Prospect', value: 'prospect' },
              { label: 'Inactief', value: 'inactive' },
            ]}
            onChange={(status) =>
              setSearch({ type: 'setFilter', payload: ['status', status] })
            }
          />
        </div>
      }
      paginationSlot={
        data?.pagination ? (
          <Pagination
            {...data.pagination}
            onLimitChange={(limit) =>
              setSearch({ type: 'setLimit', payload: limit })
            }
            onPageChange={(page) =>
              setSearch({ type: 'paginate', payload: page })
            }
          />
        ) : null
      }
    />
  );
}
